import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressPortfolioProjectsProjectsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/projects-service',
        destPath: '/api',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/projects-service',
        destPath: '/api',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/projects-service',
        destPath: '/api',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/projects-service',
        destPath: '/api',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/projects-service',
        destPath: '/api',
      },
      {
        srcPath: '/portfolio/projects/projects/api/v1/bulk/portfolio/projects',
        destPath: '/api/v1/bulk/portfolio/projects',
      },
      {
        srcPath: '/portfolio/projects/projects/api/1/portfolio/projects',
        destPath: '/api/1/portfolio/projects',
      },
    ],
    _: [
      {
        srcPath: '/_api/projects-service',
        destPath: '/api',
      },
      {
        srcPath: '/portfolio/projects/projects/api/v1/bulk/portfolio/projects',
        destPath: '/api/v1/bulk/portfolio/projects',
      },
      {
        srcPath: '/portfolio/projects/projects/api/1/portfolio/projects',
        destPath: '/api/1/portfolio/projects',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/projects-service',
        destPath: '/api',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/portfolio/v1/projects',
        destPath: '/api/v1/portfolio/projects',
      },
      {
        srcPath: '/portfolio/projects/projects/api/v1/bulk/portfolio/projects',
        destPath: '/api/v1/bulk/portfolio/projects',
      },
      {
        srcPath: '/portfolio/projects/projects/api/v1/portfolio/projects',
        destPath: '/api/v1/portfolio/projects',
      },
      {
        srcPath: '/portfolio/projects/projects/api/1/portfolio/projects',
        destPath: '/api/1/portfolio/projects',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/projects-service',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_portfolio_projects';

/** Creates a project. */
export function createProject(payload: object): RequestOptionsFactory<any> {
  function __createProject({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'project.createdDate' },
          { path: 'project.updatedDate' },
          { path: 'project.coverImage.imageInfo.urlExpirationDate' },
          { path: 'project.coverVideo.videoInfo.urlExpirationDate' },
          {
            path: 'project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
          },
          { path: 'project.coverVideo.videoInfo.posters.urlExpirationDate' },
          { path: 'project.source.lastSync' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'project.coverImage.focalPoint.x' },
          { path: 'project.coverImage.focalPoint.y' },
          { path: 'project.coverImage.unsharpMasking.amount' },
          { path: 'project.coverImage.unsharpMasking.radius' },
          { path: 'project.coverImage.unsharpMasking.threshold' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.portfolio.projects.ProjectsService.CreateProject',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/v1/portfolio/projects',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'project.createdDate' },
              { path: 'project.updatedDate' },
              { path: 'project.coverImage.imageInfo.urlExpirationDate' },
              { path: 'project.coverVideo.videoInfo.urlExpirationDate' },
              {
                path: 'project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'project.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'project.source.lastSync' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'project.coverImage.focalPoint.x' },
              { path: 'project.coverImage.focalPoint.y' },
              { path: 'project.coverImage.unsharpMasking.amount' },
              { path: 'project.coverImage.unsharpMasking.radius' },
              { path: 'project.coverImage.unsharpMasking.threshold' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createProject;
}

/** Retrieves a project. */
export function getProject(payload: object): RequestOptionsFactory<any> {
  function __getProject({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.portfolio.projects.ProjectsService.GetProject',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/v1/portfolio/projects/{projectId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'project.createdDate' },
              { path: 'project.updatedDate' },
              { path: 'project.coverImage.imageInfo.urlExpirationDate' },
              { path: 'project.coverVideo.videoInfo.urlExpirationDate' },
              {
                path: 'project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'project.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'project.source.lastSync' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'project.coverImage.focalPoint.x' },
              { path: 'project.coverImage.focalPoint.y' },
              { path: 'project.coverImage.unsharpMasking.amount' },
              { path: 'project.coverImage.unsharpMasking.radius' },
              { path: 'project.coverImage.unsharpMasking.threshold' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getProject;
}

/** Retrieves a list of all projects in a portfolio. */
export function listProjects(payload: object): RequestOptionsFactory<any> {
  function __listProjects({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.portfolio.projects.ProjectsService.ListProjects',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/v1/portfolio/projects',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'projects.createdDate' },
              { path: 'projects.updatedDate' },
              { path: 'projects.coverImage.imageInfo.urlExpirationDate' },
              { path: 'projects.coverVideo.videoInfo.urlExpirationDate' },
              {
                path: 'projects.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'projects.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'projects.source.lastSync' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'projects.coverImage.focalPoint.x' },
              { path: 'projects.coverImage.focalPoint.y' },
              { path: 'projects.coverImage.unsharpMasking.amount' },
              { path: 'projects.coverImage.unsharpMasking.radius' },
              { path: 'projects.coverImage.unsharpMasking.threshold' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listProjects;
}

/** Updates a project. */
export function updateProject(payload: object): RequestOptionsFactory<any> {
  function __updateProject({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'project.createdDate' },
          { path: 'project.updatedDate' },
          { path: 'project.coverImage.imageInfo.urlExpirationDate' },
          { path: 'project.coverVideo.videoInfo.urlExpirationDate' },
          {
            path: 'project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
          },
          { path: 'project.coverVideo.videoInfo.posters.urlExpirationDate' },
          { path: 'project.source.lastSync' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'project.coverImage.focalPoint.x' },
          { path: 'project.coverImage.focalPoint.y' },
          { path: 'project.coverImage.unsharpMasking.amount' },
          { path: 'project.coverImage.unsharpMasking.radius' },
          { path: 'project.coverImage.unsharpMasking.threshold' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.portfolio.projects.ProjectsService.UpdateProject',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/v1/portfolio/projects/{project.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'project.createdDate' },
              { path: 'project.updatedDate' },
              { path: 'project.coverImage.imageInfo.urlExpirationDate' },
              { path: 'project.coverVideo.videoInfo.urlExpirationDate' },
              {
                path: 'project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'project.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'project.source.lastSync' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'project.coverImage.focalPoint.x' },
              { path: 'project.coverImage.focalPoint.y' },
              { path: 'project.coverImage.unsharpMasking.amount' },
              { path: 'project.coverImage.unsharpMasking.radius' },
              { path: 'project.coverImage.unsharpMasking.threshold' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateProject;
}

/**
 * Updates multiple projects.
 *
 * To update a single project, call Update Project.
 */
export function bulkUpdateProjects(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateProjects({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'projects.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'projects.project.createdDate' },
          { path: 'projects.project.updatedDate' },
          { path: 'projects.project.coverImage.imageInfo.urlExpirationDate' },
          { path: 'projects.project.coverVideo.videoInfo.urlExpirationDate' },
          {
            path: 'projects.project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'projects.project.coverVideo.videoInfo.posters.urlExpirationDate',
          },
          { path: 'projects.project.source.lastSync' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'projects.project.coverImage.focalPoint.x' },
          { path: 'projects.project.coverImage.focalPoint.y' },
          { path: 'projects.project.coverImage.unsharpMasking.amount' },
          { path: 'projects.project.coverImage.unsharpMasking.radius' },
          { path: 'projects.project.coverImage.unsharpMasking.threshold' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.portfolio.projects.ProjectsService.BulkUpdateProjects',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/v1/bulk/portfolio/projects/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.project.createdDate' },
              { path: 'results.project.updatedDate' },
              {
                path: 'results.project.coverImage.imageInfo.urlExpirationDate',
              },
              {
                path: 'results.project.coverVideo.videoInfo.urlExpirationDate',
              },
              {
                path: 'results.project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.project.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'results.project.source.lastSync' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.project.coverImage.focalPoint.x' },
              { path: 'results.project.coverImage.focalPoint.y' },
              { path: 'results.project.coverImage.unsharpMasking.amount' },
              { path: 'results.project.coverImage.unsharpMasking.radius' },
              { path: 'results.project.coverImage.unsharpMasking.threshold' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateProjects;
}

/** Deletes a project. */
export function deleteProject(payload: object): RequestOptionsFactory<any> {
  function __deleteProject({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.portfolio.projects.ProjectsService.DeleteProject',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/v1/portfolio/projects/{projectId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteProject;
}

/**
 * Creates a query to retrieve a list of projects.
 *
 * The `queryProjects()` method builds a query to retrieve a list of projects and returns a `ProjectsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/find) method.
 *
 * You can refine the query by chaining `ProjectsQueryBuilder` methods onto the query. `ProjectsQueryBuilder` methods enable you to filter, sort, and control the results that `queryProjects()` returns.
 *
 * `queryProjects()`  runs with the following `ProjectsQueryBuilder` defaults which you can override:
 *
 * + [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/limit)
 * + [`ascending('_id')`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/ascending)
 *
 * The following `ProjectsQueryBuilder` methods are supported for `queryProjects()`. For a full description of the operations object, see the object returned for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-result/items) property in `ProjectsQueryResult`.
 */
export function queryProjects(payload: object): RequestOptionsFactory<any> {
  function __queryProjects({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.portfolio.projects.ProjectsService.QueryProjects',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/v1/portfolio/projects/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'projects.createdDate' },
              { path: 'projects.updatedDate' },
              { path: 'projects.coverImage.imageInfo.urlExpirationDate' },
              { path: 'projects.coverVideo.videoInfo.urlExpirationDate' },
              {
                path: 'projects.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'projects.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'projects.source.lastSync' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'projects.coverImage.focalPoint.x' },
              { path: 'projects.coverImage.focalPoint.y' },
              { path: 'projects.coverImage.unsharpMasking.amount' },
              { path: 'projects.coverImage.unsharpMasking.radius' },
              { path: 'projects.coverImage.unsharpMasking.threshold' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryProjects;
}

/**
 * Deprecated - please use ProjectsInCollectionsService.UpdateProjectOrderInCollection instead
 * our Client still use it
 * @deprecated
 */
export function updateProjectOrderInCollection(
  payload: object
): RequestOptionsFactory<any> {
  function __updateProjectOrderInCollection({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'sortOrder' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.portfolio.projects.ProjectsService.UpdateProjectOrderInCollection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath:
          '/api/v1/portfolio/projects/{projectId}/collections/{collectionId}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'project.sortOrder' },
              { path: 'projectInCollection.sortOrder' },
              { path: 'project.project.coverImage.focalPoint.x' },
              { path: 'project.project.coverImage.focalPoint.y' },
              { path: 'projectInCollection.project.coverImage.focalPoint.x' },
              { path: 'projectInCollection.project.coverImage.focalPoint.y' },
              { path: 'project.project.coverImage.unsharpMasking.amount' },
              { path: 'project.project.coverImage.unsharpMasking.radius' },
              { path: 'project.project.coverImage.unsharpMasking.threshold' },
              {
                path: 'projectInCollection.project.coverImage.unsharpMasking.amount',
              },
              {
                path: 'projectInCollection.project.coverImage.unsharpMasking.radius',
              },
              {
                path: 'projectInCollection.project.coverImage.unsharpMasking.threshold',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'project.project.createdDate' },
              { path: 'project.project.updatedDate' },
              {
                path: 'project.project.coverImage.imageInfo.urlExpirationDate',
              },
              {
                path: 'project.project.coverVideo.videoInfo.urlExpirationDate',
              },
              {
                path: 'project.project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'project.project.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'project.project.source.lastSync' },
              { path: 'projectInCollection.project.createdDate' },
              { path: 'projectInCollection.project.updatedDate' },
              {
                path: 'projectInCollection.project.coverImage.imageInfo.urlExpirationDate',
              },
              {
                path: 'projectInCollection.project.coverVideo.videoInfo.urlExpirationDate',
              },
              {
                path: 'projectInCollection.project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'projectInCollection.project.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'projectInCollection.project.source.lastSync' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateProjectOrderInCollection;
}

/**
 * Deprecated - please use ProjectsInCollectionsService.QueryProjectsInCollections instead
 * our Client still use it
 * @deprecated
 */
export function queryProjectsWithCollectionInfo(
  payload: object
): RequestOptionsFactory<any> {
  function __queryProjectsWithCollectionInfo({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.projects.v1.project',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.portfolio.projects.ProjectsService.QueryProjectsWithCollectionInfo',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioProjectsProjectsServiceUrl({
        protoPath: '/api/1/portfolio/projects/collections/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'projects.sortOrder' },
              { path: 'projects.project.coverImage.focalPoint.x' },
              { path: 'projects.project.coverImage.focalPoint.y' },
              { path: 'projects.project.coverImage.unsharpMasking.amount' },
              { path: 'projects.project.coverImage.unsharpMasking.radius' },
              { path: 'projects.project.coverImage.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'projects.project.createdDate' },
              { path: 'projects.project.updatedDate' },
              {
                path: 'projects.project.coverImage.imageInfo.urlExpirationDate',
              },
              {
                path: 'projects.project.coverVideo.videoInfo.urlExpirationDate',
              },
              {
                path: 'projects.project.coverVideo.videoInfo.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'projects.project.coverVideo.videoInfo.posters.urlExpirationDate',
              },
              { path: 'projects.project.source.lastSync' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryProjectsWithCollectionInfo;
}
